import { useCallback } from 'react'
import {
  BookingCar,
  BookingCarAttributes,
  BookingCarDiscount,
} from '~/shared/api'
import { useTaxiparkQueryParams } from '~/shared/lib/useTaxiparkQueryParams'
import {
  FetchFn,
  SCROLLABLE_LIST_META_PAGE,
  ScrollableList,
  Item,
  Sort,
} from '~/shared/ui/ScrollableList'
import { sortConfig } from './config'
import { ActionButtonsWrapper } from './styled'
import { Card } from './ui/Card'
import { Filters } from './ui/Filters'
import { Skeleton } from './ui/Skeleton'
import { useExternalFilters } from './useExternalFilters'

type Row = Item &
  BookingCarAttributes & { taxiparkName: string } & {
    discount?: BookingCarDiscount
  }

export const QUERY_KEY_LIST_BOOKING_CARS = 'list-booking-cars'

export function ListBookingCars() {
  const { xTaxiparkKeyQuery } = useTaxiparkQueryParams()
  const { externalFilters } = useExternalFilters()

  const fetchRows = useCallback<FetchFn<Row>>(
    async (page, pageSize, filters, sort) => {
      let builder = BookingCar.limit(pageSize).with('taxipark')

      sort && (builder = builder.orderBy(sort))

      filters?.forEach(({ key, value }) => {
        builder = builder.where(key, value)
      })

      const response = await builder.get(page)
      const data = response.getData()

      const items = data.map((bookingCar) => {
        return {
          id: bookingCar?.getApiId() as string,
          ...bookingCar.getAttributes(),
          taxiparkName: bookingCar?.getTaxipark()?.getName(),
          discount: bookingCar.getDiscount(),
          digitalbox: bookingCar.getDigitalBox(),
        }
      })

      const httpClientResponse = response.getHttpClientResponse()
      const axiosResponse = httpClientResponse?.getUnderlying()
      const { to, currentPage, lastPage } =
        axiosResponse?.data?.meta?.page || SCROLLABLE_LIST_META_PAGE

      return {
        items,
        to,
        currentPage,
        lastPage,
      }
    },
    [],
  )

  const handleMapItems = useCallback(
    (items: Row[]) => (
      <>
        {items.map(({ id, taxiparkName, discount, ...attr }) => (
          <div style={{ marginBottom: '24px' }} key={id}>
            <Card
              carId={id}
              carAttributes={attr}
              xTaxiparkKey={xTaxiparkKeyQuery}
              taxiparkName={taxiparkName}
              discount={discount}
            />
          </div>
        ))}
      </>
    ),
    [xTaxiparkKeyQuery],
  )

  return (
    <>
      <ActionButtonsWrapper>
        <Sort options={sortConfig} />
        <Filters />
      </ActionButtonsWrapper>

      <ScrollableList
        queryKey={QUERY_KEY_LIST_BOOKING_CARS}
        externalFilters={externalFilters}
        fetchFn={fetchRows}
        mapItems={handleMapItems}
        skeletonNode={<Skeleton />}
        skeletonItemMarginBottom='24px'
        empty={
          <div style={{ fontSize: '18px', marginTop: '40px' }}>
            По выбранным фильтрам автомобилей не найдено!
          </div>
        }
      />
    </>
  )
}
